import { Link } from '@tanstack/react-router';
import type { StravaStatus } from '@painpal/contracts';
import { ChevronDown, ChevronRight, RefreshCw, Upload, X } from 'lucide-react';
import { useRef, useState, type FocusEvent, type RefObject } from 'react';
import type { SportFilter } from '../../api/keys';
import { useStravaSync } from '../../api/mutations';
import { errorMessage } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { BottomSheet } from '../../ui/BottomSheet';
import { Presence } from '../../ui/Presence';
import { useModalFocus } from '../../ui/useModalFocus';
import { sportSearch } from './activitiesModel';
import styles from './activities.module.css';

function ItemText({ title, sub }: { title: string; sub: string }) {
  return (
    <span className={styles.menuText}>
      <span className={styles.menuTitle}>{title}</span>
      <span className={styles.menuSub}>{sub}</span>
    </span>
  );
}

function SyncItem({ strava, onSync, onNavigate }: { strava: StravaStatus | undefined; onSync: () => void; onNavigate: () => void }) {
  if (strava?.connected !== true) {
    return (
      <Link role="menuitem" to="/settings" className={styles.menuItem} onClick={onNavigate}>
        <RefreshCw size={16} aria-hidden="true" /><ItemText title="CONNECT STRAVA" sub="Set it up in Settings" />
        <ChevronRight className={styles.menuChevron} size={16} aria-hidden="true" />
      </Link>
    );
  }
  const sub = strava.lastSyncAt === null ? 'Not synced yet' : `Last synced ${formatRelative(strava.lastSyncAt, new Date())}`;
  return (
    <button role="menuitem" type="button" className={styles.menuItem} onClick={onSync}>
      <RefreshCw size={16} aria-hidden="true" /><ItemText title="SYNC STRAVA NOW" sub={sub} />
      <ChevronRight className={styles.menuChevron} size={16} aria-hidden="true" />
    </button>
  );
}

interface MenuSheetProps { sport: SportFilter; strava: StravaStatus | undefined; onSync: () => void; onClose: () => void }

function MenuSheet({ sport, strava, onSync, onClose }: MenuSheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  useModalFocus(ref, onClose);
  return (
    <BottomSheet onClose={onClose}>
      <div ref={ref} tabIndex={-1} className={styles.popover} data-motion="popover">
        <div className={styles.sheetHead}>
          <div className={styles.sheetText}>
            <p className={styles.sheetEyebrow}>ACTIVITIES</p>
            <h2 className={styles.sheetTitle}>ADD ACTIVITY</h2>
          </div>
          <button type="button" className="icon-button" aria-label="Close add menu" onClick={onClose}><X size={16} aria-hidden="true" /></button>
        </div>
        <div className={styles.menu} role="menu" aria-label="Add activity">
          <Link role="menuitem" to="/activities" search={{ ...sportSearch(sport), upload: true }} className={styles.menuItem} data-tone="pink" onClick={onClose}>
            <Upload size={16} aria-hidden="true" /><ItemText title="UPLOAD .FIT FILES" sub="Garmin, Wahoo, Zwift exports" />
            <ChevronRight className={styles.menuChevron} size={16} aria-hidden="true" />
          </Link>
          <SyncItem strava={strava} onSync={onSync} onNavigate={onClose} />
        </div>
        <button type="button" className={styles.sheetCancel} onClick={onClose}>CANCEL</button>
      </div>
    </BottomSheet>
  );
}

export interface AddActivityMenuProps { sport: SportFilter; strava: StravaStatus | undefined; buttonRef: RefObject<HTMLButtonElement | null> }

export function AddActivityMenu({ sport, strava, buttonRef }: AddActivityMenuProps) {
  const [open, setOpen] = useState(false);
  const sync = useStravaSync();
  const close = (): void => setOpen(false);
  function onBlur(event: FocusEvent<HTMLDivElement>): void {
    if (!event.currentTarget.contains(event.relatedTarget)) close();
  }
  return (
    <div className={styles.addWrap} onBlur={onBlur}>
      <button ref={buttonRef} type="button" className={`btn btn-ink ${styles.addButton}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>+ ADD <span data-part="long" className="sr-only-phone">ACTIVITY</span></span>
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      <Presence show={open}>
        <MenuSheet sport={sport} strava={strava} onSync={() => { sync.mutate(); close(); }} onClose={close} />
      </Presence>
      {sync.isSuccess && <p role="status" className={styles.syncNote}>SYNC QUEUED · NEW RIDES APPEAR SHORTLY</p>}
      {sync.isError && <p role="alert" className={styles.syncNote}>{errorMessage(sync.error)}</p>}
    </div>
  );
}
